// RecipePairingCard — wine / cocktail / non-alcoholic suggestions for
// a saved recipe. Lives between the recipe page's nutrition line and
// tags row.
//
// Cost discipline: the AI call is OPT-IN. A first-time card renders
// the "Suggest a pairing" CTA only — no network call until the user
// taps. After generation the result is persisted on the recipe row
// (recipe.pairings via useRecipeBook.setPairings) so subsequent views
// render from cache with zero network cost. A small "Try again"
// affordance overwrites the cache if the user wants a re-roll.
//
// Reads pairings from `recipe.recipe.pairings` (the JSONB blob).
// Schema:
//   {
//     wine:         { name, style, rationale },
//     cocktail:     { name, style, rationale } | null,
//     nonAlcoholic: { name, style, rationale } | null
//   }
// Cocktail / non-alcoholic slots hide gracefully when null — gives
// the model permission to skip a slot when it has nothing useful.

import { useState } from "react";
import { FONT } from "../lib/tokens";
import { suggestWinePairing } from "../lib/suggestWinePairing";

export default function RecipePairingCard({ recipe, onSetPairings, viewerAllergies = [] }) {
  const cached = recipe?.recipe?.pairings || null;
  const [busy, setBusy]   = useState(false);
  const [errMsg, setErr]  = useState("");
  // Filled-state expand toggle. Default collapsed: once generated,
  // the user wants the recipe page back. Tap the header to peel it
  // open when actually pouring something.
  const [expanded, setExpanded] = useState(false);

  const fire = async () => {
    if (busy) return;
    setBusy(true);
    setErr("");
    const { pairings, error } = await suggestWinePairing({
      allergies: viewerAllergies,
      recipe: recipe?.recipe || {},
    });
    if (error) {
      setErr(error);
      setBusy(false);
      return;
    }
    try {
      await onSetPairings?.(pairings);
    } catch (e) {
      setErr(e?.message || "Couldn't save pairing");
    }
    setBusy(false);
  };

  // Empty state — no pairings yet. Single CTA. No network on render.
  if (!cached) {
    return (
      <div style={containerStyle}>
        <div style={kickerStyle}>PAIRS WITH</div>
        <button
          onClick={fire}
          disabled={busy}
          style={ctaStyle(busy)}
          aria-label="Suggest a pairing for this recipe"
        >
          {busy ? "Pairing…" : "Suggest a pairing"}
        </button>
        {errMsg && <ErrorLine msg={errMsg} />}
      </div>
    );
  }

  // Filled state — collapsed by default. Tap header to expand.
  // Recipe-page real estate is limited; once generated, the user
  // wants pairings to live as a peeled-back summary they can pull
  // open when actually pouring. Refresh button stays in the header
  // so it's reachable from either collapsed or expanded state.
  const summary = [
    cached.wine?.name,
    cached.cocktail?.name,
    cached.nonAlcoholic?.name,
  ].filter(Boolean).join(" · ");
  return (
    <div style={containerStyle}>
      <button
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse pairings" : "Expand pairings"}
        style={collapseHeaderStyle}
      >
        <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          <div style={kickerStyle}>PAIRS WITH</div>
          {!expanded && summary && (
            <div style={summaryLineStyle}>{summary}</div>
          )}
        </div>
        <span style={chevronStyle}>{expanded ? "▴" : "▾"}</span>
      </button>
      {expanded && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
            <PairingRow label="WINE"          pick={cached.wine} />
            <PairingRow label="COCKTAIL"      pick={cached.cocktail} />
            <PairingRow label="NON-ALCOHOLIC" pick={cached.nonAlcoholic} />
          </div>
          <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={fire}
              disabled={busy}
              aria-label="Try a different pairing"
              style={refreshBtnStyle(busy)}
            >
              {busy ? "Trying…" : "↻ Try again"}
            </button>
          </div>
        </>
      )}
      {errMsg && <ErrorLine msg={errMsg} />}
    </div>
  );
}

function PairingRow({ label, pick }) {
  if (!pick || !pick.name) return null;
  return (
    <div>
      <div style={rowKickerStyle}>{label}</div>
      <div style={rowNameStyle}>{pick.name}</div>
      {pick.style && <div style={rowStyleStyle}>{pick.style}</div>}
      {pick.rationale && (
        <div style={rowRationaleStyle}>{pick.rationale}</div>
      )}
    </div>
  );
}

function ErrorLine({ msg }) {
  return (
    <div style={{
      marginTop: 8,
      fontFamily: FONT.forma, fontSize: 11, color: "#A04230",
    }}>
      {msg}
    </div>
  );
}

// ── Styling ─────────────────────────────────────────────────────────

const containerStyle = {
  marginTop: 10,
  padding: "12px 14px",
  background: "rgba(255,252,241,0.65)",
  border: "1px solid rgba(124,94,44,0.22)",
  borderRadius: 10,
};

const kickerStyle = {
  fontFamily: FONT.forma, fontSize: 9, fontWeight: 600,
  color: "#9C7E40", letterSpacing: "0.18em",
};

const kickerHeaderStyle = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  marginBottom: 10,
};

const ctaStyle = (busy) => ({
  marginTop: 8,
  width: "100%",
  padding: "10px 12px",
  background: busy ? "rgba(124,94,44,0.18)" : "transparent",
  border: "1px solid rgba(124,94,44,0.40)",
  borderRadius: 8,
  fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
  letterSpacing: "0.10em", textTransform: "uppercase",
  color: "#5C3F18",
  cursor: busy ? "default" : "pointer",
});

const collapseHeaderStyle = {
  display: "flex", alignItems: "center", gap: 10,
  width: "100%",
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  textAlign: "left",
};

const summaryLineStyle = {
  marginTop: 3,
  fontFamily: FONT.itemTitle, fontSize: 14,
  fontStyle: "italic", color: "#3A2A12",
  lineHeight: 1.25,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const chevronStyle = {
  flexShrink: 0,
  fontSize: 14, color: "#7C5E2C",
  fontFamily: FONT.forma,
};

const refreshBtnStyle = (busy) => ({
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "6px 10px",
  background: "transparent",
  border: "1px solid rgba(124,94,44,0.28)",
  borderRadius: 999,
  color: "#7C5E2C",
  fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
  letterSpacing: "0.10em", textTransform: "uppercase",
  cursor: busy ? "default" : "pointer",
});

const rowKickerStyle = {
  fontFamily: FONT.forma, fontSize: 8, fontWeight: 600,
  color: "#9C7E40", letterSpacing: "0.16em",
  marginBottom: 2,
};

const rowNameStyle = {
  fontFamily: FONT.itemTitle, fontSize: 16,
  fontStyle: "italic", color: "#3A2A12",
  lineHeight: 1.2,
};

const rowStyleStyle = {
  marginTop: 1,
  fontFamily: FONT.forma, fontSize: 11,
  color: "#7C5E2C",
};

const rowRationaleStyle = {
  marginTop: 3,
  fontFamily: FONT.forma, fontSize: 12,
  color: "#5C3F18",
  lineHeight: 1.4,
};
